import React from 'react';
import { deleteSignature, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, } from '@Store';
import { CrudEnum, IPreviewSignature, PermissionEnum, EnumTranslateTitle, EnumListSearchProps, TSignatureSortableListCol, CannedAnswerChannelEnum, SignatureTypeEnum, } from 'atlas-shared';
import { tableColumnDatetimeSorter, tableColumnBooleanRender, tableColumnStringSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchLikeProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const SignatureListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewSignature) => React.ReactElement> = [], filters: IWithListProps<IPreviewSignature, TSignatureSortableListCol>['filters'], sorter: IWithListProps<IPreviewSignature, TSignatureSortableListCol>['sorter']): ColumnsType<IPreviewSignature> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewSignature['organization_id'], signature: IPreviewSignature) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      filteredValue: filters.title || null,
      ...GetColumnSearchLikeProps(t, 'title', t('TITLE')),
      className: 'table-cell-title',
    },
    {
      title: t('CHANNEL'),
      dataIndex: 'channel',
      key: 'channel',
      render: (channel: IPreviewSignature['channel'], signature: IPreviewSignature) => EnumTranslateTitle('CANNED_ANSWER_CHANNEL', channel, t),
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => EnumTranslateTitle('CANNED_ANSWER_CHANNEL', a.channel, t).localeCompare(EnumTranslateTitle('CANNED_ANSWER_CHANNEL', b.channel, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'channel')?.order || null,
      filteredValue: filters.channel || null,
      ...GetColumnSearchProps(t, 'channel', t('CHANNEL'), EnumListSearchProps('CANNED_ANSWER_CHANNEL', CannedAnswerChannelEnum, t)),
      className: 'table-cell-channel',
    },
    {
      title: t('TYPE'),
      dataIndex: 'type',
      key: 'type',
      render: (type: IPreviewSignature['type'], signature: IPreviewSignature) => EnumTranslateTitle('SIGNATURE_TYPE', type, t),
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => EnumTranslateTitle('SIGNATURE_TYPE', a.type, t).localeCompare(EnumTranslateTitle('SIGNATURE_TYPE', b.type, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'type')?.order || null,
      filteredValue: filters.type || null,
      ...GetColumnSearchProps(t, 'type', t('TYPE'), EnumListSearchProps('SIGNATURE_TYPE', SignatureTypeEnum, t)),
      className: 'table-cell-type',
    },
    {
      title: t('GLOBAL'),
      dataIndex: 'global',
      key: 'global',
      render: (global: IPreviewSignature['global'], signature: IPreviewSignature) => tableColumnBooleanRender(t, global),
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => (a.global ? 1 : 0) > (b.global ? 1 : 0) ? 1 : -1,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'global')?.order || null,
      filteredValue: filters.global || null,
      ...GetColumnSearchProps(t, 'global', t('GLOBAL'), [{ id: true, title: t('YES'), }, { id: false, title: t('NO'), }, ]),
      className: 'table-cell-global',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewSignature['created_at'], signature: IPreviewSignature) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    showDeleted && { 
      title: t('DELETED'), 
      dataIndex: 'deleted_at',
      key: 'deleted_at',
      className: 'table-cell-deleted_at',
      render: (deleted_at: IPreviewSignature['deleted_at']) => deleted_at ? timeSince(t, new Date(deleted_at), true) : '',
      sorter: (a: IPreviewSignature, b: IPreviewSignature) => tableColumnDatetimeSorter(a.deleted_at, b.deleted_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'deleted_at')?.order || null,
    },
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewSignature) => (
        <div className={'table-operation-wrp'}>
          {!row.deleted_at && buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.Signature, row) && listCloneButton(t, navigate, 'signature', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'signature', row)}
          {!row.deleted_at && abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.Signature, row) && listEditButton(t, navigate, 'signature', row)}
          {!row.deleted_at && abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.Signature, row) && listDeleteButton(t, row, deleteSignature)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewSignature>;

};
